import type {
  AdminNotificationRequest,
  AdminNotificationResponse,
  NotificationsListResponse,
  NotificationItem,
  NotificationMarkReadResponse,
  NotificationMarkAllReadResponse
} from "@/types";

import { atom } from "jotai";
import { atomWithQuery } from "jotai-tanstack-query";

import { apiClient } from "@/utils/api";
import { buildApiParams } from "@/utils/helpers";
import { createPaginationParams, createTableQueryOptions } from "@/components/table";

// Shared params atom for notifications list
export type NotificationsListParams = {
  page: number;
  per_page: number;
  filters: Record<string, string | undefined>;
  refresh?: number;
};

export const notificationsListParamsAtom = atom<NotificationsListParams>({
  ...createPaginationParams(),
  filters: {},
});

// Hydration flag to delay initial query until URL/filters are synced
export const notificationsHydratedAtom = atom<boolean>(false);

// GET /notifications list query
export const notificationsQueryAtom = atomWithQuery<NotificationsListResponse>((get) => {
  const params = get(notificationsListParamsAtom);
  const hydrated = get(notificationsHydratedAtom);

  return createTableQueryOptions({
    queryKey: ["notifications", params] as const,
    queryFn: async () => {
      const apiParams = buildApiParams(
        { page: params.page, per_page: params.per_page },
        params.filters || {}
      );

      const { data } = await apiClient.get("/notifications", { params: apiParams });

      return data as NotificationsListResponse;
    },
    enabled: hydrated
  });
});

// Mark single notification as read
export const markNotificationRead = atom(
  null,
  async (get, set, notificationId: string) => {
    const { data } = await apiClient.put(`/notifications/${notificationId}/read`);

    // Trigger refresh by updating params
    const currentParams = get(notificationsListParamsAtom);
    set(notificationsListParamsAtom, { ...currentParams, refresh: Date.now() });

    return data as NotificationMarkReadResponse;
  }
);

// Mark all notifications as read
export const markAllNotificationsRead = atom(
  null,
  async (get, set) => {
    const { data } = await apiClient.post("/notifications/read-all");

    // Trigger refresh by updating params
    const currentParams = get(notificationsListParamsAtom);
    set(notificationsListParamsAtom, { ...currentParams, refresh: Date.now() });

    return data as NotificationMarkAllReadResponse;
  }
);

// Send admin notification
export const sendAdminNotification = atom(
  null,
  async (_get, _set, payload: AdminNotificationRequest) => {
    const { data } = await apiClient.post("/notifications/send", payload);

    return data as AdminNotificationResponse;
  }
);

// Real-time notification state for SSE
export const realtimeNotificationsAtom = atom<NotificationItem[]>([]);

// Add new notification from SSE
export const addRealtimeNotification = atom(
  null,
  (get, set, notification: NotificationItem) => {
    const current = get(realtimeNotificationsAtom);
    set(realtimeNotificationsAtom, [notification, ...current]);
  }
);

// Update notification from SSE
export const updateRealtimeNotification = atom(
  null,
  (get, set, notification: NotificationItem) => {
    const current = get(realtimeNotificationsAtom);
    const updated = current.map(n => n.id === notification.id ? notification : n);
    set(realtimeNotificationsAtom, updated);
  }
);

// Remove notification from SSE
export const removeRealtimeNotification = atom(
  null,
  (get, set, notificationId: string) => {
    const current = get(realtimeNotificationsAtom);
    const filtered = current.filter(n => n.id !== notificationId);
    set(realtimeNotificationsAtom, filtered);
  }
);

// Clear all real-time notifications
export const clearRealtimeNotifications = atom(
  null,
  (get, set) => {
    set(realtimeNotificationsAtom, []);
  }
);
